import { Collapse, InteractiveContainer } from "@jugl-web/ui-kit";
import { isoToLocalDate, useTranslations } from "@jugl-web/utils";
import { formatSecondsToCompactDuration } from "@jugl-web/utils/date-time/formatSecondsToCompactDuration";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import React, { useMemo, useState } from "react";
import { VoiceConversationHistoryItemModel } from "@jugl-web/rest-api";
import differenceInSeconds from "date-fns/differenceInSeconds";
import isValid from "date-fns/isValid";
import { VoiceConversationMediaPlayer } from "../../../../../VoiceConversationSummary/components/VoiceConversationMediaPlayer";
import { VoiceConversationTopicPill } from "../../../../../VoiceConversationSummary/components/VoiceConversationTopicPill";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as SparkleIcon } from "./assets/sparkle.svg";

/**
 * These timestamps come out of `JSON.parse(dto.payload)` and are only *typed*
 * as ISO strings — nothing validates them. `isoToLocalDate` silently returns an
 * `Invalid Date` for malformed input and date-fns `format()` throws
 * `RangeError: Invalid time value` on it, which would take down the whole
 * history list rather than a single row. Parse defensively.
 */
const toValidLocalDate = (value: string | undefined): Date | null => {
  if (!value) {
    return null;
  }

  const date = isoToLocalDate(value);

  return isValid(date) ? date : null;
};

export const VoiceHistoryItem: React.FC<VoiceConversationHistoryItemModel> = ({
  mediaId,
  insertedAt,
  summary,
  topic,
  startedAt,
  endedAt,
}) => {
  const { t } = useTranslations();
  const { formatDateLabel, formatTimeLabel } = useFormattedDate();
  const [isOpen, setIsOpen] = useState(false);

  const hasExpandableContent = Boolean(summary || mediaId);

  const headerDate = useMemo(
    () => toValidLocalDate(insertedAt) || toValidLocalDate(startedAt),
    [insertedAt, startedAt]
  );

  const callDetails = useMemo(() => {
    const startedAtDate = toValidLocalDate(startedAt);
    const endedAtDate = toValidLocalDate(endedAt);

    if (!startedAtDate || !endedAtDate) {
      return null;
    }

    const durationInSeconds = differenceInSeconds(endedAtDate, startedAtDate);

    // Clock skew / bad data can put the end before the start. Rendering a
    // clamped "0:00" would be a plausible-looking lie, so fall back instead
    if (durationInSeconds < 0) {
      return null;
    }

    return {
      durationLabel: formatSecondsToCompactDuration(durationInSeconds),
      timeRangeLabel: t(
        {
          id: "client-support-page.voice-call-time-range",
          defaultMessage: "{startTime} – {endTime}",
        },
        {
          startTime: formatTimeLabel(startedAtDate),
          endTime: formatTimeLabel(endedAtDate),
        }
      ),
    };
  }, [startedAt, endedAt, formatTimeLabel, t]);

  return (
    <div className="rounded-[20px] border border-solid border-[#E2E8F0] p-5">
      <InteractiveContainer
        onClick={() => setIsOpen((prev) => !prev)}
        isDisabled={!hasExpandableContent}
        className="flex flex-col gap-1.5"
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-4">
            <span className="truncate font-secondary text-sm leading-[140%] font-medium tracking-[0.14px] text-dark">
              {headerDate ? formatDateLabel(headerDate) : null}
            </span>
            {hasExpandableContent && (
              <ChevronIcon className={isOpen ? "rotate-180" : undefined} />
            )}
          </div>
          {callDetails ? (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 font-secondary text-xs leading-[140%]">
              <span className="font-medium text-dark-700">
                {t(
                  {
                    id: "client-support-page.voice-call-duration",
                    defaultMessage: "Call duration: {duration}",
                  },
                  { duration: callDetails.durationLabel }
                )}
              </span>
              <span className="text-grey-900">
                {callDetails.timeRangeLabel}
              </span>
            </div>
          ) : (
            headerDate && (
              <span className="font-secondary text-xs leading-[140%] text-grey-900">
                {formatTimeLabel(headerDate)}
              </span>
            )
          )}
        </div>
        <VoiceConversationTopicPill topic={topic} disableTooltip />
      </InteractiveContainer>
      <Collapse
        isOpen={isOpen}
        unmountOnExit
        className="flex flex-col gap-4 pt-8"
      >
        {mediaId ? <VoiceConversationMediaPlayer mediaId={mediaId} /> : null}
        {summary ? (
          <div className="flex flex-col gap-1.5 rounded-lg border border-solid border-grey-300 p-3.5">
            <div className="flex items-center gap-1.5">
              <SparkleIcon className="shrink-0" />
              <span className="font-secondary text-sm font-medium text-dark">
                {t({
                  id: "client-support-page.voice-summary-label",
                  defaultMessage: "Summary:",
                })}
              </span>
            </div>
            <span className="jugl__break-word font-secondary text-xs text-dark-700">
              {summary}
            </span>
          </div>
        ) : null}
      </Collapse>
    </div>
  );
};
